import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Back, User, Map, Check, X } from '../../assets/icons';
import '../../assets/css/admin/case-detail.css';
import { casesApi } from '../../network/apis/cases';
import { doctorApi } from '../../network/apis/doctors';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/elements/LoadingSpinner';
// import { set } from 'lodash';

const AdminCaseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [caseData, setCaseData] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [searchDoctor, setSearchDoctor] = useState('');
    const [newNote, setNewNote] = useState('');
    const [addingNote, setAddingNote] = useState(false);

    const fetchCaseDetails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await casesApi.getCase(id);
            if (response != null) {
                setCaseData(response);
                return response;
            } else {
                setCaseData(null);
                toast.error('Case not found');
            }
        } catch (error) {
            setCaseData(null);
            console.error('Failed to fetch case:', error);
            toast.error('Failed to load case details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchDoctors = useCallback(async () => {
        try {
            const response = await doctorApi.getDoctors();
            if (response?.data) {
                // Filter only verified doctors
                const verifiedDoctors = response.data.filter(d => d.is_verified);
                setDoctors(verifiedDoctors);
                return verifiedDoctors;
            }
        } catch (error) {
            console.error('Failed to fetch doctors:', error);
        }
        finally {
            // setDoctors([]);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCaseDetails();
        fetchDoctors();
    }, [fetchCaseDetails, fetchDoctors]);


    const handleAssignDoctor = async () => {
        if (!selectedDoctor) {
            toast.error('Please select a doctor');
            return;
        }

        try {
            setAssigning(true);
            await casesApi.assignDoctor(id, selectedDoctor);
            toast.success('Doctor assigned successfully');
            setShowAssignModal(false);
            setSelectedDoctor(null);
            fetchCaseDetails(); // Refresh case data
        } catch (error) {
            console.error('Failed to assign doctor:', error);
            toast.error(error.response?.data?.message || 'Failed to assign doctor');
        } finally {
            setAssigning(false);
        }
    };

    const handleResolveCase = async () => {
        try {
            await casesApi.resolveCase(id);
            toast.success('Case marked as resolved');
            fetchCaseDetails();
        } catch (error) {
            toast.error('Failed to resolve case');
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) {
            toast.error('Please enter a note');
            return;
        }

        try {
            setAddingNote(true);
            await casesApi.addNote(id, { content: newNote });
            toast.success('Note added successfully');
            setNewNote('');
            fetchCaseDetails();
        } catch (error) {
            toast.error('Failed to add note');
        } finally {
            setAddingNote(false);
        }
    };

    const filteredDoctors = doctors.filter(doctor => {
        const fullName = `${doctor.first_name || ''} ${doctor.last_name || ''}`.toLowerCase();
        const specialization = (doctor.specialization || '').toLowerCase();
        const search = searchDoctor.toLowerCase();
        return fullName.includes(search) || specialization.includes(search);
    });

    const getStatusColor = (status) => {
        const colors = {
            'PENDING': 'warning',
            'ASSIGNED': 'info',
            'IN_PROGRESS': 'primary',
            'RESOLVED': 'success',
            'CLOSED': 'success',
            'REJECTED': 'danger',
            'CANCELLED': 'danger'
        };
        return colors[status] || 'default';
    };

    if (loading) {
        return (
            <div className="admin-case-detail">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading case details...</p>
                </div>
            </div>
        );
    }

    if (!caseData) {
        return (
            <div className="admin-case-detail">
                <div className="error-state">
                    <p>Case not found</p>
                    <Button text="Go Back" onClick={() => navigate('/admin/cases')} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-case-detail">
            <div className="page-header">
                <div className="header-left">
                    <button className="back-btn" onClick={() => navigate('/admin/cases')}>
                        <img src={Back} alt="Back" />
                    </button>
                    <div className="header-content">
                        <h1>Case #{caseData.case_code || caseData.id}</h1>
                        <p>{caseData.title}</p>
                    </div>
                </div>
                <div className="header-actions">
                    {caseData.status === 'PENDING' && (
                        <Button
                            text="Assign Doctor"
                            variant="primary"
                            onClick={() => setShowAssignModal(true)}
                        />
                    )}
                    {['IN_PROGRESS', 'ASSIGNED'].includes(caseData.status) && (
                        <Button
                            text="Mark Resolved"
                            variant="success"
                            onClick={handleResolveCase}
                        />
                    )}
                </div>
            </div>

            <div className="case-content">
                <div className="main-section">
                    {/* Case Overview */}
                    <div className="detail-card">
                        <h3>Case Overview</h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Status</label>
                                <span className={`status-badge ${getStatusColor(caseData.status)}`}>
                                    {caseData.status?.replace('_', ' ')}
                                </span>
                            </div>
                            <div className="info-item">
                                <label>Priority</label>
                                <span className={`priority-badge ${caseData.priority?.toLowerCase()}`}>
                                    {caseData.priority || 'Normal'}
                                </span>
                            </div>
                            <div className="info-item">
                                <label>Created</label>
                                <span>{caseData.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <label>Last Updated</label>
                                <span>{caseData.updated_at ? new Date(caseData.updated_at).toLocaleDateString() : 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="detail-card">
                        <h3>Description</h3>
                        <p className="description-text">{caseData.description || 'No description provided'}</p>
                    </div>

                    {/* Symptoms */}
                    {caseData.symptoms && caseData.symptoms.length > 0 && (
                        <div className="detail-card">
                            <h3>Reported Symptoms</h3>
                            <div className="symptoms-list">
                                {caseData.symptoms.map((symptom, index) => (
                                    <span key={index} className="symptom-tag">{symptom}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Case Images */}
                    {caseData.images && caseData.images.length > 0 && (
                        <div className="detail-card">
                            <h3>Case Images</h3>
                            <div className="images-grid">
                                {caseData.images.map((img, index) => (
                                    <div key={index} className="case-image">
                                        <img src={img.image} alt={`Case ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Notes Section */}
                    <div className="detail-card">
                        <h3>Case Notes</h3>
                        <div className="add-note-form">
                            <textarea
                                placeholder="Add a note..."
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                rows={3}
                            />
                            <Button
                                text={addingNote ? 'Adding...' : 'Add Note'}
                                variant="outline"
                                onClick={handleAddNote}
                                disabled={addingNote}
                            />
                        </div>
                        <div className="notes-list">
                            {caseData.notes && caseData.notes.length > 0 ? (
                                caseData.notes.map((note, index) => (
                                    <div key={index} className="note-item">
                                        <div className="note-header">
                                            <span className="note-author">{note.author?.email || 'System'}</span>
                                            <span className="note-date">
                                                {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                                            </span>
                                        </div>
                                        <p className="note-content">{note.content}</p>
                                    </div>
                                ))
                            ) : (
                                <p className="no-notes">No notes yet</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="sidebar-section">
                    {/* Farmer Info */}
                    <div className="detail-card">
                        <h3>Farmer Information</h3>
                        <div className="person-info">
                            <div className="person-avatar">
                                <img src={User} alt="" />
                            </div>
                            <div className="person-details">
                                <strong>{caseData.farmer_name} {caseData.farmer?.last_name}</strong>
                                <span>{caseData.farmer?.email}</span>
                                <span>{caseData.farmer?.phone_number}</span>
                            </div>
                        </div>
                    </div>

                    {/* Assigned Doctor */}
                    <div className="detail-card">
                        <h3>Assigned Doctor</h3>
                        {caseData.doctor ? (
                            <div className="person-info">
                                <div className="person-avatar">
                                    <img src={User} alt="" />
                                </div>
                                <div className="person-details">
                                    <strong>Dr. {caseData.doctor_name} {caseData.doctor?.last_name}</strong>
                                    <span>{caseData.doctor?.specialization}</span>
                                    <span>{caseData.doctor?.email}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="no-assignment">
                                <p>No doctor assigned yet</p>
                                <Button
                                    text="Assign Doctor"
                                    variant="primary"
                                    small
                                    onClick={() => setShowAssignModal(true)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Location */}
                    <div className="detail-card">
                        <h3>Location</h3>
                        <div className="location-info">
                            <img src={Map} alt="" />
                            <div>
                                <span>{caseData.location || caseData.farm?.address || 'Location not specified'}</span>
                                {caseData.latitude && caseData.longitude && (
                                    <small>Coordinates: {caseData.latitude}, {caseData.longitude}</small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Assign Doctor Modal */}
            {showAssignModal && (
                <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Assign Doctor to Case</h2>
                            <button className="close-btn" onClick={() => setShowAssignModal(false)}>
                                <img src={X} alt="Close" />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="search-input">
                                <input
                                    type="text"
                                    placeholder="Search doctors by name or specialization..."
                                    value={searchDoctor}
                                    onChange={(e) => setSearchDoctor(e.target.value)}
                                />
                            </div>
                            <div className="doctors-list">
                                {filteredDoctors.length > 0 ? (
                                    filteredDoctors.map(doctor => (
                                        <div
                                            key={doctor.id}
                                            className={`doctor-option ${selectedDoctor === doctor.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedDoctor(doctor.id)}
                                        >
                                            <div className="doctor-info">
                                                <strong>Dr. {doctor.first_name} {doctor.last_name}</strong>
                                                <span>{doctor.specialization}</span>
                                            </div>
                                            {selectedDoctor === doctor.id && (
                                                <img src={Check} alt="Selected" className="check-icon" />
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-doctors">No doctors found</p>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button
                                text="Cancel"
                                variant="outline"
                                onClick={() => setShowAssignModal(false)}
                            />
                            <Button
                                text={assigning ? 'Assigning...' : 'Assign Doctor'}
                                variant="primary"
                                onClick={handleAssignDoctor}
                                disabled={!selectedDoctor || assigning}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCaseDetail;
